// Ported from mobile src/hooks/useAnalysisSubscription.ts (import paths only).
import { useEffect, useRef, useCallback } from 'react';
import { useAnalysisWsStore } from 'store/useAnalysisWsStore';
import { makeSubKey } from 'types/analysisWs';
import type { BrainerCoreInner } from 'types/brainerCore';
import type { AnalysisSubStatus } from 'types/analysisWs';

const INITIAL_PUSH_TIMEOUT_MS = 10_000;

export interface UseAnalysisSubscriptionResult {
  data: BrainerCoreInner | null;
  status: AnalysisSubStatus;
  /** True while waiting for the first push (status is 'idle' or 'loading'). */
  isLoading: boolean;
  isReady: boolean;
  isTimeout: boolean;
  /** Re-sends the subscribe message and resets the 10 s timeout window. */
  retry: () => void;
}

/**
 * Subscribe to real-time BrainerCore pushes for a (symbol, timeframe, market,
 * exchange) combination.
 *
 * - Sends `subscribe` when the WS is open (or queues for when it reconnects).
 * - Shows loading state until the first push arrives (max 10 s).
 * - On timeout expiry exposes `isTimeout` so callers can render a retry option.
 * - Multiple components calling this hook for the same key share one
 *   subscription via ref-counting; the WS `unsubscribe` is only sent when the
 *   last consumer unmounts.
 */
export function useAnalysisSubscription(
  symbol: string,
  timeframe: string,
  market: 'futures' | 'spot' = 'futures',
  exchange: 'binance' | 'mexc' = 'binance',
): UseAnalysisSubscriptionResult {
  // Wire/canonical form — server echoes timeframe lowercase, so store keys and
  // outbound payloads must use the lowercase form to match inbound pushes.
  const tfWire = timeframe.toLowerCase();
  const key = makeSubKey(symbol, tfWire, market, exchange);

  // Keep latest values in refs so async callbacks don't go stale.
  const symbolRef = useRef(symbol);
  const timeframeRef = useRef(tfWire);
  const marketRef = useRef(market);
  const exchangeRef = useRef(exchange);
  symbolRef.current = symbol;
  timeframeRef.current = tfWire;
  marketRef.current = market;
  exchangeRef.current = exchange;

  const store = useAnalysisWsStore();

  // ── 1. Register subscription + increment ref-count on mount ───────────────
  useEffect(() => {
    // Snapshot at effect-run time so cleanup unsubscribes the correct pair/TF.
    const capturedSymbol = symbol;
    const capturedTimeframe = tfWire;
    store.ensureSubscription(key);
    store.incrementSubscriber(key);

    return () => {
      const isLast = store.decrementSubscriber(key);
      if (isLast) {
        const msg = {
          action: 'unsubscribe' as const,
          symbol: capturedSymbol,
          timeframe: capturedTimeframe,
          market: marketRef.current,
          exchange: exchangeRef.current,
        };
        // Use getState() so we always send on the live socket, not a stale
        // closure copy captured before a reconnect.
        const wire = useAnalysisWsStore.getState().sendMessage;
        if (wire) {
          wire(msg);
        } else {
          // Socket is mid-reconnect or backgrounded — buffer so the server
          // doesn't end up with an orphan subscription.
          useAnalysisWsStore.getState().queueUnsubscribe(msg);
        }
        store.removeSubscription(key);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // ── 2. Send subscribe whenever sendMessage becomes available ───────────────
  const sendMessage = store.sendMessage;
  useEffect(() => {
    if (!sendMessage) return;
    store.resetToLoading(key);
    sendMessage({ action: 'subscribe', symbol, timeframe: tfWire, market, exchange });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendMessage, key]);

  // ── 3. 10 s timeout for initial push ──────────────────────────────────────
  const currentStatus = store.subscriptions[key]?.status;
  useEffect(() => {
    if (currentStatus !== 'loading') return;
    if (!sendMessage) return;

    const timer = setTimeout(() => {
      useAnalysisWsStore.getState().markTimeout(key);
    }, INITIAL_PUSH_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, currentStatus, sendMessage]);

  // ── 4. Retry ───────────────────────────────────────────────────────────────
  const retry = useCallback(() => {
    useAnalysisWsStore.getState().resetToLoading(key);
    useAnalysisWsStore.getState().sendMessage?.({
      action: 'subscribe',
      symbol: symbolRef.current,
      timeframe: timeframeRef.current,
      market: marketRef.current,
      exchange: exchangeRef.current,
    });
  }, [key]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const sub = store.subscriptions[key];
  const status: AnalysisSubStatus = sub?.status ?? 'idle';

  return {
    data: sub?.data ?? null,
    status,
    isLoading: status === 'idle' || status === 'loading',
    isReady: status === 'ready',
    isTimeout: status === 'timeout',
    retry,
  };
}
